import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { isNilOrEmptyString } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getLabelMessage } from '@gef-ui/localization/messages';
import ChevronDown from '@gef-ui/material-icons/ChevronDown';
import ChevronUp from '@gef-ui/material-icons/ChevronUp';
import ClockFive from '@gef-ui/material-icons/ClockFive';

import { PKUrl } from 'components';
import { Flame } from 'components/atoms/CustomSVGIcon';
import { subheaders } from 'features/common/Subheader/constants';
import { getVisibleStateById } from 'features/common/Subheader/selectors';
import { setOnDetailThroughMarketPlaceRow } from 'features/views/marketPlace/actions';
import { feedbackType, quickFilter } from 'features/views/marketPlace/constants';
import { getQuickFilterState } from 'features/views/marketPlace/selectors';
import {
	generateInfoCircleTooltip,
	generateLeadIcon,
	generateRemainingHoursColor,
} from 'features/views/marketPlace/utils';
import { equalOrThreeDashes, getRoutePath, phoneToLink } from 'utils';
import { generateFullNameOrBusinessName } from 'utils/string';

import LeadFeedback from './LeadFeedback';
import RemainingTimeBarMui from './RemainingTimeBarMui';

const MarketPlaceTableRow = ({ record, isDetailVisible, onDetailVisibilityChange }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const {
		taskTypeName,
		taskSubtypeName,
		client = {},
		id,
		clientLoadedFromCcd,
		taskTypeId,
		taskSubtypeId,
		lastNote,
		statusId,
		finishTypeId,
		isFromMarket,
		sla,
		totalHours,
		remainingHours,
	} = record;
	const { id: partnerId, businessName, surname, firstName, phone, kclPhone, email, kclEmail, address = {} } = client;

	const finalEmail = kclEmail || email;
	const quickFilterState = useSelector(getQuickFilterState);
	const subheaderOpen = useSelector((state) => getVisibleStateById(state, subheaders.marketPlace, false));
	const isCurrentFilterOrSubheaderOpen = quickFilterState === quickFilter.CURRENT || subheaderOpen;
	const showSLA = isFromMarket && !isNilOrEmptyString(sla) && isNilOrEmptyString(finishTypeId);

	const remainingHoursOrZero = remainingHours < 0 ? 0 : remainingHours;
	const remainingHoursColor = generateRemainingHoursColor({ totalHours, remainingHours: remainingHoursOrZero });

	const handleRedirectToDetail = useCallback(
		(leadId) => () => {
			const { pathname, state } = getRoutePath('leadDetail', { leadId }, true);
			navigate(pathname, { state });
			dispatch(setOnDetailThroughMarketPlaceRow(true));
		},
		[dispatch, navigate],
	);

	const addHotIcon = (isFromMarket) =>
		isFromMarket && (
			<div className="HotIcon">
				<Flame /> {getLabelMessage('marketPlace:hot')}
			</div>
		);

	const handleToggleFeedback = useCallback(
		(e) => {
			e.stopPropagation();
			onDetailVisibilityChange();
		},
		[onDetailVisibilityChange],
	);

	return (
		<>
			<TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
				<TableCell>{generateLeadIcon({ taskTypeId, taskSubtypeId, width: 35, height: 35 })}</TableCell>
				<TableCell>
					<div>{equalOrThreeDashes(taskTypeName)}</div>
					<div>{equalOrThreeDashes(taskSubtypeName)}</div>
				</TableCell>
				<TableCell>{addHotIcon(isFromMarket)}</TableCell>
				<TableCell>
					<PKUrl.PKPartner partnerId={clientLoadedFromCcd && partnerId} taskId={id} muiLink unlock>
						{generateFullNameOrBusinessName(businessName, surname, firstName)}
					</PKUrl.PKPartner>
				</TableCell>
				<TableCell>
					<div>{equalOrThreeDashes(address.municipality)}</div>
					<div>{equalOrThreeDashes(address.zipCode)}</div>
				</TableCell>
				<TableCell>
					<div>{phoneToLink(kclPhone || phone, true)}</div>
				</TableCell>
				<TableCell sx={{ wordBreak: 'break-all', borderBottom: 'unset' }}>
					<Link underline="none" href={`mailTo:${finalEmail}`}>
						{finalEmail}
					</Link>
				</TableCell>
				{isCurrentFilterOrSubheaderOpen && (
					<TableCell>
						{showSLA && (
							<Tooltip title={getLabelMessage('marketPlace:slaIconText')}>
								<IconButton sx={{ color: remainingHoursColor, cursor: 'default', p: 0 }} disableRipple>
									<ClockFive />
								</IconButton>
							</Tooltip>
						)}
					</TableCell>
				)}
				{(quickFilterState !== quickFilter.FUTURE || subheaderOpen) && (
					<TableCell>
						<RemainingTimeBarMui
							remainingTimeData={{
								totalHours,
								remainingHours,
								sla,
								isFromMarket,
								statusId,
								finishTypeId,
							}}
						/>
					</TableCell>
				)}
				<TableCell>
					<div>{generateInfoCircleTooltip(lastNote)}</div>
				</TableCell>
				<TableCell>
					<Link underline="none" onClick={handleRedirectToDetail(id)}>
						<Box sx={{ color: 'primary.main', fontWeight: 'bold' }}>{getLabelMessage('common:detail')}</Box>
					</Link>
				</TableCell>
				<TableCell>
					{isDetailVisible ? (
						<ChevronUp
							sx={{ color: 'primary.main', cursor: 'pointer' }}
							id="toggleIcon"
							onClick={handleToggleFeedback}
						/>
					) : (
						<ChevronDown
							sx={{ color: 'primary.main', cursor: 'pointer' }}
							id="toggleIcon"
							onClick={handleToggleFeedback}
						/>
					)}
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell sx={{ py: 0 }} colSpan={12}>
					<Collapse in={isDetailVisible} unmountOnExit>
						<Box sx={{ mt: 1, mb: 2 }}>
							<LeadFeedback lead={record} type={feedbackType.IN_LIST} />
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
};

MarketPlaceTableRow.propTypes = {
	isDetailVisible: PropTypes.bool,
	onDetailVisibilityChange: PropTypes.func,
	record: PropTypes.object,
};

export default MarketPlaceTableRow;
